import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { useField } from "formik"
import { isEqual } from "lodash-es"

import type { NullableCalendarEvent } from "./calendar/fullCalendarTypes"
import type { OrganizerSelectedValue, ResourceItem } from "../../createOrderTypes"
import type { MeetingServiceOrder } from "../../../../../modules/merchant/accounting/accounting/accountingTypes"
import type { CreateOrderDialogFlow } from "../../createOrderTypes"
import { CreateOrderFlowType } from "../../createOrderTypes"
import { defaultInitialEventState } from "../../defaults"
import { getBookingResourceWithTableSetup } from "../form/helpers"
import { getOrderPerson } from "../../../../utils/getOrderPerson"
import {
    CreateUpdateBookingAttendee,
    CreateUpdateBookingResourceTableSetups,
} from "../../../dialogs/createUpdateBookingDialog/createUpdateBookingTypes"

export type InitialValuesState = {
    id: number | null
    meetingName: string
    organizers: OrganizerSelectedValue
    attendees: CreateUpdateBookingAttendee[]
    resources: CreateUpdateBookingResourceTableSetups[]
    messageToServiceCenter?: string
    isPrivate?: boolean
    isExternal?: boolean
}

export type InitialEventState = {
    event: NullableCalendarEvent
    initValues: InitialValuesState | null
    isUpdated: boolean
}

type HandleChangeInitEventStateParams = {
    event?: InitialEventState["event"]
    initValues?: InitialEventState["initValues"]
    isUpdated?: InitialEventState["isUpdated"]
    fullResources?: ResourceItem[]
}

type ResourceBookingDraftDataValue = Readonly<{
    draftEvent: NullableCalendarEvent
    fullResources: ResourceItem[]
    initialEventStateId?: number | null
    isUpdatedOrder: boolean
}>

type ResourceBookingDraftActionsValue = Readonly<{
    setDraftEvent: (event: NullableCalendarEvent, fullResources?: ResourceItem[]) => void
    setAndSyncDraftEvent: (event: NullableCalendarEvent, resources: ResourceItem[]) => void
    syncDraftToFormik: () => void
    handleChangeInitEventState: (params: HandleChangeInitEventStateParams) => void
    handleCancelClick: () => void
}>

export type ResourceBookingDraftContextValue = ResourceBookingDraftDataValue & ResourceBookingDraftActionsValue

const ResourceBookingDraftDataContext = createContext<ResourceBookingDraftDataValue | null>(null)
const ResourceBookingDraftActionsContext = createContext<ResourceBookingDraftActionsValue | null>(null)

type ResourceBookingDraftProviderProps = Readonly<{
    children: React.ReactNode
    orderDetails?: MeetingServiceOrder
    flow?: CreateOrderDialogFlow["flow"]
}>

export function ResourceBookingDraftProvider({ children, orderDetails, flow }: ResourceBookingDraftProviderProps) {
    const [{ value: formikEvent }, , eventHelpers] = useField<NullableCalendarEvent>("event")
    const [{ value: formikFullResources }, , fullResourcesHelpers] = useField<ResourceItem[]>("fullResourcesList")

    const eventHelpersRef = useRef(eventHelpers)
    eventHelpersRef.current = eventHelpers
    const fullResourcesHelpersRef = useRef(fullResourcesHelpers)
    fullResourcesHelpersRef.current = fullResourcesHelpers

    const [draftEvent, setDraftEventState] = useState<NullableCalendarEvent>(formikEvent ?? null)
    const [fullResources, setFullResourcesState] = useState<ResourceItem[]>(formikFullResources ?? [])
    const [isUpdatedOrder, setIsUpdatedOrder] = useState<boolean>(defaultInitialEventState.isUpdated)

    const draftEventRef = useRef(draftEvent)
    draftEventRef.current = draftEvent
    const fullResourcesRef = useRef(fullResources)
    fullResourcesRef.current = fullResources

    const refInitialEvent = useRef<NullableCalendarEvent>(defaultInitialEventState.event)
    const refInitialFullResources = useRef<ResourceItem[]>([])
    const refInitValues = useRef<InitialValuesState | null>(defaultInitialEventState.initValues)
    const isOutlookFlow = flow === CreateOrderFlowType.OUTLOOK
    const initialEventStateId = refInitValues.current?.id

    const setDraftEvent = useCallback((event: NullableCalendarEvent, nextFullResources?: ResourceItem[]) => {
        setDraftEventState(event)
        if (nextFullResources !== undefined) {
            setFullResourcesState(nextFullResources)
        } else if (!event) {
            setFullResourcesState([])
        }
    }, [])

    useEffect(() => {
        const initValues = refInitValues.current
        if (initValues && draftEvent && !isEqual(draftEvent, refInitialEvent.current)) {
            setIsUpdatedOrder(true)
        }
    }, [draftEvent])

    useEffect(() => {
        if (orderDetails) {
            const { id, meetingDetails, bookingDetails, messageToServiceCenter } = orderDetails
            const { person } = getOrderPerson(orderDetails.organizers)
            // Use formikEvent/formikFullResources from closure — Formik is expected to have
            // updated values by the time this effect runs (same render cycle as orderDetails change).
            refInitialEvent.current = formikEvent ?? null
            refInitialFullResources.current = formikFullResources ?? []
            refInitValues.current = {
                id,
                meetingName: meetingDetails.meetingName || "",
                organizers: person!,
                attendees: meetingDetails.attendees || [],
                resources: bookingDetails?.resources ? getBookingResourceWithTableSetup(bookingDetails.resources) : [],
                messageToServiceCenter: messageToServiceCenter ?? "",
                isPrivate: meetingDetails.isPrivate || false,
                isExternal: meetingDetails.isExternal || false,
            }
            setIsUpdatedOrder(false)
        }
    }, [orderDetails?.id])

    useEffect(() => {
        setDraftEventState(formikEvent ?? null)
        setFullResourcesState(formikFullResources ?? [])
    }, [orderDetails?.id, formikEvent, formikFullResources])

    const handleCancelClick = useCallback(() => {
        const initValues = refInitValues.current
        const shouldResetToInitialEvent = initValues?.id || (!initValues?.id && isOutlookFlow)

        if (shouldResetToInitialEvent) {
            const initialEvent = refInitialEvent.current
            const initialFullResources = refInitialFullResources.current
            setDraftEvent(initialEvent ?? null, initialEvent ? initialFullResources : undefined)
            setIsUpdatedOrder(false)
        } else {
            setDraftEvent(null)
        }
    }, [isOutlookFlow, setDraftEvent])

    const syncDraftToFormik = useCallback(() => {
        eventHelpersRef.current.setValue(draftEventRef.current)
        fullResourcesHelpersRef.current.setValue(fullResourcesRef.current)
    }, [])

    const setAndSyncDraftEvent = useCallback((event: NullableCalendarEvent, resources: ResourceItem[]) => {
        setDraftEventState(event)
        setFullResourcesState(resources)
        eventHelpersRef.current.setValue(event)
        fullResourcesHelpersRef.current.setValue(resources)
    }, [])

    const handleChangeInitEventState = useCallback(
        ({ event, initValues, isUpdated, fullResources }: HandleChangeInitEventStateParams) => {
            if (event !== undefined) refInitialEvent.current = event
            if (initValues !== undefined) refInitValues.current = initValues
            if (fullResources !== undefined) refInitialFullResources.current = fullResources
            if (isUpdated !== undefined) setIsUpdatedOrder(isUpdated)
        },
        [],
    )

    const dataValue = useMemo<ResourceBookingDraftDataValue>(
        () => ({
            draftEvent,
            fullResources,
            initialEventStateId,
            isUpdatedOrder,
        }),
        [draftEvent, fullResources, initialEventStateId, isUpdatedOrder],
    )

    const actionsValue = useMemo<ResourceBookingDraftActionsValue>(
        () => ({
            setDraftEvent,
            setAndSyncDraftEvent,
            syncDraftToFormik,
            handleChangeInitEventState,
            handleCancelClick,
        }),
        [setDraftEvent, setAndSyncDraftEvent, syncDraftToFormik, handleChangeInitEventState, handleCancelClick],
    )

    return (
        <ResourceBookingDraftDataContext.Provider value={dataValue}>
            <ResourceBookingDraftActionsContext.Provider value={actionsValue}>
                {children}
            </ResourceBookingDraftActionsContext.Provider>
        </ResourceBookingDraftDataContext.Provider>
    )
}

export function useResourceBookingDraft(): ResourceBookingDraftContextValue {
    const data = useContext(ResourceBookingDraftDataContext)
    const actions = useContext(ResourceBookingDraftActionsContext)
    if (!data || !actions) {
        throw new Error("useResourceBookingDraft must be used within ResourceBookingDraftProvider")
    }
    return { ...data, ...actions }
}

export function useResourceBookingDraftData(): ResourceBookingDraftDataValue {
    const context = useContext(ResourceBookingDraftDataContext)
    if (!context) {
        throw new Error("useResourceBookingDraftData must be used within ResourceBookingDraftProvider")
    }
    return context
}

export function useResourceBookingDraftActions(): ResourceBookingDraftActionsValue {
    const context = useContext(ResourceBookingDraftActionsContext)
    if (!context) {
        throw new Error("useResourceBookingDraftActions must be used within ResourceBookingDraftProvider")
    }
    return context
}
